import React from 'react';
import Icon from './Icon';
import { Notification, notificationTimeAgo } from '../api/notifications';

interface NotificationRowProps {
  notification: Notification;
  /** Separator above every row but the first. */
  showSeparator: boolean;
  /** Where this row navigates, or null when it has no destination. */
  to: string | null;
  /** Called with the resolved path when an actionable row is activated. */
  onActivate: (notification: Notification, path: string) => void;
}

/**
 * One row of the notification dropdown, shared by the admin and agent layouts
 * so the two panels cannot drift apart.
 *
 * Rows with a destination behave as buttons — clickable, focusable and
 * activated by Enter or Space. Rows without one stay inert rather than looking
 * clickable and doing nothing.
 */
const NotificationRow: React.FC<NotificationRowProps> = ({
  notification: n,
  showSeparator,
  to,
  onActivate,
}) => {
  const isActionable = to !== null;
  const activate = () => {
    if (to) onActivate(n, to);
  };

  return (
    <div
      className="flex items-start gap-3"
      role={isActionable ? 'button' : undefined}
      tabIndex={isActionable ? 0 : undefined}
      onClick={isActionable ? activate : undefined}
      onKeyDown={
        isActionable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              }
            }
          : undefined
      }
      style={{
        // Full-bleed hover inside the panel's 18px scroll padding.
        margin: '0 -18px',
        padding: '11px 18px',
        borderTop: showSeparator ? '1px solid var(--g100)' : 'none',
        opacity: n.isRead ? 0.65 : 1,
        cursor: isActionable ? 'pointer' : 'default',
        transition: 'background .12s ease',
      }}
      onMouseEnter={(e) => {
        if (isActionable) e.currentTarget.style.background = 'var(--g50)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: 34,
          height: 34,
          background:
            n.color === 'green' ? 'var(--green-bg, #e7f7ef)' : 'var(--brand-bg)',
        }}
      >
        <Icon
          name={n.icon || 'bell'}
          size={15}
          color={n.color === 'green' ? 'var(--green)' : 'var(--brand)'}
        />
      </div>
      <div className="flex-1 min-w-0">
        {n.title && (
          <div className="font-semibold text-ink" style={{ fontSize: 13 }}>
            {n.title}
          </div>
        )}
        <div
          className="text-g600"
          style={{ fontSize: 12, marginTop: n.title ? 2 : 0 }}
        >
          {n.text}
        </div>
        <div className="text-g400" style={{ fontSize: 11, marginTop: 3 }}>
          {notificationTimeAgo(n.createdAt)}
        </div>
      </div>
      {/*
       * Unread marker. Previously nested inside the title block, so untitled
       * notifications never showed one even when unread.
       */}
      {!n.isRead && (
        <span
          className="rounded-full flex-shrink-0"
          aria-label="Unread"
          style={{
            width: 6,
            height: 6,
            marginTop: 6,
            background: 'var(--brand)',
          }}
        />
      )}
    </div>
  );
};

export default NotificationRow;
